/**
 * FleetHub Zustand Store
 *
 * State management for B2B fleet management portal.
 * Handles order listing, pagination, modal states, and map data.
 *
 * @module stores/useFleetHubStore
 */

import * as Sentry from "@sentry/react"
import { create } from "zustand"
import { devtools } from "zustand/middleware"

import type { SortDirectionOption, DateRange } from "@/components/FilterBar.web"
import { isB2bAccessDenied } from "@/services/firebase/b2bAccess"
import type { HubLocation, CustomerLocationData } from "@/services/firebase/functions"
import { MultiOrderPusherService } from "@/services/pusher/multi-order-pusher-service"
import { PusherService } from "@/services/pusher/pusher-service"
import type {
  LocationUpdate,
  StageUpdate,
  DriverStatusUpdate,
  NumberPlateUpdate,
  LiveETAUpdate,
} from "@/services/pusher/pusher-service"
import { isTutorialDone, markTutorialDone } from "@/utils/tutorialStorage"

/**
 * Injectable loader for the Firebase callables module (PLA-433 testability seam).
 *
 * Every store action pulls its callables in lazily via `_functionsModuleLoader
 * .load()` instead of a bare `await import(...)`. In production this is exactly a
 * dynamic `import("@/services/firebase/functions")` — the web build still code-
 * splits the callables into their own chunk and the store still avoids a
 * circular import at module-load time. The only difference is the seam: jest
 * runs without `--experimental-vm-modules`, so it cannot execute a native
 * dynamic `import()`; tests replace `.load` with a resolved mock module to drive
 * the polling / sync-handoff logic. Runtime behaviour is unchanged.
 */
export const _functionsModuleLoader = {
  load: (): Promise<typeof import("@/services/firebase/functions")> =>
    import("@/services/firebase/functions"),
}

/**
 * Callable rejection codes that mean "the server refused this booking on
 * purpose" rather than "the server is unwell". `createFleetHubOrder` rewraps the
 * `FirebaseError` while preserving `.code` (PLA-534), so both the namespaced SDK
 * form and the bare form can arrive here.
 */
const BOOKING_REJECTION_CODES: ReadonlySet<string> = new Set([
  "functions/failed-precondition",
  "failed-precondition",
  "functions/invalid-argument",
  "invalid-argument",
])

/**
 * The customer-ready German message for a deliberate server-side booking
 * rejection, or `null` when the failure is not one — in which case the caller
 * keeps the generic outage copy. Validation tier per ADR-0036.
 */
function bookingRejectionMessage(error: unknown): string | null {
  const code = (error as { code?: unknown } | null | undefined)?.code
  if (typeof code !== "string" || !BOOKING_REJECTION_CODES.has(code)) return null

  const message = (error as { message?: unknown }).message
  return typeof message === "string" && message.trim().length > 0 ? message : null
}

/**
 * Fleet Order interface matching backend FirestoreOrder
 */
export interface FleetOrder {
  // Core identifiers
  id: string
  hubspotTicketId: string

  // Customer & Vehicle
  customerNumber: string
  customerName: string
  numberPlate: string
  companyName?: string
  childCompanyId?: string // Child company ID for parent-child company support
  childCompanyName?: string // Child company name for display

  // Service Details
  orderType?: string // HU/AU, §19, NK, §21, etc.
  serviceType: "pickup_delivery" | "driveby"
  zustandigePrufstelle?: string // Inspection center name

  // Location Data
  hubLocation?: {
    id: string
    name: string
    address: string
    latitude: number
    longitude: number
  }
  customerLocation?: {
    address: string
    latitude: number
    longitude: number
  }

  // Status & Timing
  status: string // new, confirmed, picking_up, at_hub, etc.
  stage?: number // Numeric stage (1-6) for tracking
  orderPhase?: string
  scheduledPickupTime?: string // ISO string
  scheduledReturnTime?: string // ISO string
  spatesteAbholung?: string // HH:MM format
  spatesteRuckgabe?: string // HH:MM format

  // Inspection Results
  inspectionPassed?: boolean | null
  inspectionResult?: string // OM, GM, HW, EM, VM, VU
  berichtPdfStatus?: "pending" | "available" | "error"
  berichtPdfId?: string

  // Actions & Tracking
  trackingToken?: string
  ticketRescheduling?: string
  ticketCancellation?: string

  // Live ETA (refreshed by Pusher ETA_UPDATE during driving phases, plus
  // initial values forwarded by the getFleetOrders callable).
  liveEstimatedCompletionMinutes?: number
  projectedCompletionAtLive?: string // ISO timestamp
  // ADR-0012: predicted PA door-arrival. HBS-only; absent until backend ships.
  // Frozen once realisedPickupAt arrives or status leaves picking_up.
  projectedPickupAtLive?: string | null // ISO timestamp
  // ADR-0011: confirmed handover moment. HBS-only; absent on drivebyss.
  realisedPickupAt?: string | null // ISO timestamp
  // PLA-307: actual completion timestamp (HubSpot erledigt_timestamp).
  // Only present on completed orders synced after the backend ships this field.
  completedAt?: string // ISO-8601 actual completion

  // Last-known driver position, joined from the publicOrderTracking mirror by
  // the getFleetOrders callable. Seeds the FleetHub map car on page reload
  // before the first live Pusher tick; live LOCATION_UPDATE events overwrite it.
  currentDriverLocation?: {
    latitude: number
    longitude: number
    timestamp: number
    isReturn: boolean
  } | null

  // Metadata
  createdAt: Date
  updatedAt: Date
}

/**
 * Driver location data for live tracking
 */
export interface DriverLocationData {
  latitude: number
  longitude: number
  timestamp: number
  heading?: number
  speed?: number
  isReturn: boolean
}

/**
 * Active order tracking data
 */
export interface ActiveOrderTrackingData {
  orderId: string
  status: string
  serviceType: "pickup_delivery" | "driveby"
  driverLocation: DriverLocationData | null
  hubLocation: HubLocation | null
  customerLocation: { latitude: number; longitude: number } | null
  orderPhase: number
  numberPlate: string
  customerName?: string
  customerAddress?: string
}

/**
 * Filter State Interface (simplified - always sorts by pickup date)
 */
export interface FilterState {
  sortDirection: SortDirectionOption
  dateRange: DateRange
}

/**
 * Draft of a booking-in-flight (PLA-433).
 *
 * Captured by BookInspectionModal immediately before the booking call and
 * held in the store so the ghost order card can render a real row layout
 * (known fields + shimmer for the server-assigned ones) while the
 * HubSpot→Firestore sync completes. Never forwarded to the backend — the
 * booking callable receives its own args.
 */
export interface PendingOrderDraft {
  abholdatum: string
  spatesteAbholung: string
  serviceType: "pickup_delivery" | "driveby"
  numberPlate?: string
  companyName?: string
  nachkontrolle: boolean
}

/**
 * The single in-flight row mutation (cancel or reschedule) (PLA-433).
 *
 * Drives the per-row pending overlay + brand check pulse. `phase` advances
 * pending → synced when the poll detects the change; the store then clears it
 * to null after the ~800ms pulse dwell.
 */
export interface PendingRowAction {
  orderId: string
  type: "cancel" | "reschedule"
  newDate?: string
  newTime?: string
  phase: "pending" | "synced"
}

/**
 * FleetHub State Interface
 */
interface FleetHubState {
  // Data
  orders: FleetOrder[]
  currentPage: number
  pageSize: number
  totalCount: number
  hasMore: boolean

  // Filter State
  filters: FilterState

  // Map Data
  customerLocations: CustomerLocationData[]
  hubLocations: HubLocation[]
  isLoadingMapData: boolean
  mapDataError: string | null

  // Live Tracking State
  activeOrders: Map<string, ActiveOrderTrackingData>
  pusherService: MultiOrderPusherService | null
  isTrackingActive: boolean
  trackingError: string | null
  failedOrderSubscriptions: string[] // Order IDs that failed to subscribe

  // Search State
  numberPlateFilter: string // Normalized plate for filtering (empty = no filter)

  // UI State
  isLoading: boolean
  error: string | null

  /**
   * Set when the backend's B2B guard rejects this session (PLA-534). The screen
   * watches it and signs the user out — without it a non-B2B account sits on a
   * dashboard where every callable 403s, forever, because the Firebase session
   * itself is valid and browserLocalPersistence keeps it alive.
   */
  b2bAccessRevoked: boolean

  // Password Change Prompt
  changedInitialPassword: boolean
  showPasswordPrompt: boolean
  passwordPromptDismissed: boolean
  dismissPasswordPrompt: () => void
  markPasswordChanged: () => Promise<void>

  // Modals
  showRescheduleModal: boolean
  showCancelModal: boolean
  showBookingModal: boolean
  bookingNachkontrollePreselected: boolean
  selectedOrder: FleetOrder | null

  // Booking State
  isBookingOrder: boolean
  bookingError: string | null
  showLoadingCard: boolean
  loadingCardTimeout: NodeJS.Timeout | null
  pollingIntervals: NodeJS.Timeout[]
  // Session-level Pusher client subscribed to `pending-order-{ticketId}` between
  // createFleetHubOrder returning and the new order appearing in the orders list.
  // Cleared by hideLoadingCard once the order is visible (or the failsafe times out).
  pendingOrderPusher: PusherService | null
  // Ghost-card sync UX (PLA-433). `pendingOrderDraft` carries the known booking
  // data so the ghost card renders a real row layout; `loadingCardPhase` drives
  // the shimmer → check-pulse handoff; `loadingCardSyncTimeout` defers the list
  // swap ~800ms so the brand check pulse plays before the ghost is replaced by
  // the real (highlighted) row.
  pendingOrderDraft: PendingOrderDraft | null
  loadingCardPhase: "pending" | "synced"
  loadingCardSyncTimeout: NodeJS.Timeout | null

  // Reschedule / Cancel State (modals read isReschedulingOrder/isCancellingOrder
  // + the error fields for their in-flight/error UI).
  isReschedulingOrder: boolean
  rescheduleError: string | null
  isCancellingOrder: boolean
  cancelError: string | null
  // Single in-flight row mutation (cancel or reschedule) driving the per-row
  // pending overlay + check pulse (PLA-433). Timers live in the store so they
  // survive row unmount / pagination. Single-pending: arming a new row action
  // clears any prior one first.
  pendingRowAction: PendingRowAction | null
  pendingRowPollInterval: ReturnType<typeof setInterval> | null
  pendingRowSyncTimeout: NodeJS.Timeout | null
  pendingRowFailsafeTimeout: NodeJS.Timeout | null

  // Highlight State (for card glow animation on updates)
  highlightedOrderId: string | null

  // Actions
  loadOrders: (page?: number) => Promise<void>
  loadMapData: () => Promise<void>
  setPage: (page: number) => void

  // Search Actions
  searchByPlate: (normalizedPlate: string) => void
  clearSearch: () => void

  // Filter Actions
  setFilters: (filters: FilterState) => void
  resetFilters: () => void
  applyFilters: () => Promise<void>
  openRescheduleModal: (order: FleetOrder) => void
  closeRescheduleModal: () => void
  openCancelModal: (order: FleetOrder) => void
  closeCancelModal: () => void
  openBookingModal: () => void
  openBookingModalWithNachkontrolle: () => void
  closeBookingModal: () => void
  createFleetOrder: (data: {
    abholdatum: string
    spatesteAbholung: string
    nachkontrolle: boolean
    walkIn?: boolean
    childCompanyId?: string
    numberPlate?: string
    inspectionType?: import("@/services/firebase/functions").InspectionType
    customerNotes?: string
  }) => Promise<void>
  /**
   * Run post-booking side-effects (Pusher subscription, polling, loading card)
   * without closing the modal. Used by the document-upload path in BookInspectionModal
   * so the modal can remain open for the attach step before handing off control.
   * (PLA-122 / ADR-0022)
   */
  startPostBookingFlow: (ticketId: string) => Promise<void>
  rescheduleFleetOrder: (
    orderId: string,
    data: { abholdatum: string; spatesteAbholung: string },
  ) => Promise<void>
  cancelFleetOrder: (orderId: string) => Promise<void>
  checkForNewOrder: () => Promise<void>
  hideLoadingCard: () => void
  setPendingOrderDraft: (draft: PendingOrderDraft) => void
  hidePendingRowAction: () => void
  highlightOrder: (orderId: string) => void
  reset: () => void

  // Live Tracking Actions
  initializeLiveTracking: () => Promise<void>
  stopLiveTracking: () => void
  addOrderToTracking: (order: FleetOrder) => Promise<void>
  removeOrderFromTracking: (orderId: string) => void
  syncTracking: () => void
  updateDriverLocation: (orderId: string, location: DriverLocationData) => void
  updateOrderStatus: (orderId: string, status: string) => void
  updateOrderNumberPlate: (orderId: string, numberPlate: string) => void
  updateOrderLiveETA: (
    orderId: string,
    // `projectedCompletionAtLive` is optional because the backend
    // live_return_eta feature flag may have stripped it from the payload.
    // `projectedPickupAtLive` is the symmetric pickup-side field (ADR-0012 §5)
    // and follows the same kill-switch rule via the `live_pickup_eta` flag.
    // When undefined, the existing field on the order is preserved (the
    // backend's job to send `null` if it wants to clear; for now we treat
    // absence as "no update", matching the Pusher-payload reality).
    update: {
      // Optional because pickup-only fan-outs (heartbeat tick during
      // `picking_up`) omit it — liveMinutes semantically belongs to the
      // return leg and is meaningless during pickup decay.
      liveMinutes?: number
      projectedCompletionAtLive?: string
      projectedPickupAtLive?: string
    },
  ) => void
  // PLA-25: realised pickup moment from Pusher PICKUP_REALIZED.
  // First-write-wins: once set, subsequent broadcasts are ignored.
  updateOrderRealisedPickup: (orderId: string, realisedPickupAt: string) => void
  // PLA-111: backend fires Pusher REPORT_AVAILABLE when berichtPdfStatus
  // transitions into "available". Idempotent in practice (backend predicate
  // fires once per order's PDF lifetime), but the action is safe to re-call:
  // it only writes when the field isn't already "available".
  markOrderReportAvailable: (orderId: string) => void
  isOrderActive: (order: FleetOrder) => boolean
  needsLiveTracking: (order: FleetOrder) => boolean

  // Onboarding Tutorial
  showTutorial: boolean
  currentTutorialStep: number
  initializeTutorial: () => void
  nextTutorialStep: () => void
  dismissTutorial: () => void
}

/**
 * Request counter to prevent stale loadOrders responses from overwriting newer results.
 * Incremented at the start of each loadOrders call; responses are discarded if the
 * counter has moved on (meaning a newer request was fired).
 */
let _loadOrdersRequestId = 0

/**
 * Default filter state (always sorts by pickup date, no active date filter)
 */
const defaultFilters: FilterState = {
  sortDirection: "desc", // Newest first
  dateRange: { startDate: null, endDate: null }, // No date filtering
}

/**
 * Initial state values
 */
const initialState = {
  orders: [],
  currentPage: 1,
  pageSize: 20,
  totalCount: 0,
  hasMore: false,
  filters: defaultFilters,
  numberPlateFilter: "",
  customerLocations: [],
  hubLocations: [],
  isLoadingMapData: false,
  mapDataError: null,
  activeOrders: new Map<string, ActiveOrderTrackingData>(),
  pusherService: null,
  isTrackingActive: false,
  trackingError: null,
  failedOrderSubscriptions: [],
  isLoading: false,
  error: null,
  b2bAccessRevoked: false,
  changedInitialPassword: true,
  showPasswordPrompt: false,
  passwordPromptDismissed: false,
  showRescheduleModal: false,
  showCancelModal: false,
  showBookingModal: false,
  bookingNachkontrollePreselected: false,
  selectedOrder: null,
  isBookingOrder: false,
  bookingError: null,
  showLoadingCard: false,
  loadingCardTimeout: null,
  pollingIntervals: [],
  pendingOrderPusher: null,
  pendingOrderDraft: null,
  loadingCardPhase: "pending" as const,
  loadingCardSyncTimeout: null,
  isReschedulingOrder: false,
  rescheduleError: null,
  isCancellingOrder: false,
  cancelError: null,
  pendingRowAction: null,
  pendingRowPollInterval: null,
  pendingRowSyncTimeout: null,
  pendingRowFailsafeTimeout: null,
  highlightedOrderId: null,
  showTutorial: false,
  currentTutorialStep: 0,
}

/**
 * Check if date filter is active
 */
function hasActiveDateFilter(dateRange: DateRange): boolean {
  return dateRange.startDate !== null && dateRange.endDate !== null
}

/**
 * Clear the three timers backing a pending row action (PLA-433): the 3s poll
 * interval, the ~800ms synced-dwell timeout, and the 30s failsafe. Safe to call
 * when any are null; callers reset the handles to null afterwards.
 */
function _clearPendingRowTimers(
  s: Pick<
    FleetHubState,
    "pendingRowPollInterval" | "pendingRowSyncTimeout" | "pendingRowFailsafeTimeout"
  >,
): void {
  if (s.pendingRowPollInterval) clearInterval(s.pendingRowPollInterval)
  if (s.pendingRowSyncTimeout) clearTimeout(s.pendingRowSyncTimeout)
  if (s.pendingRowFailsafeTimeout) clearTimeout(s.pendingRowFailsafeTimeout)
}

/**
 * FleetHub Store
 *
 * @example
 * ```tsx
 * // Subscribe to specific slices (optimized re-renders)
 * const orders = useFleetHubStore(state => state.orders);
 * const isLoading = useFleetHubStore(state => state.isLoading);
 * const loadOrders = useFleetHubStore(state => state.loadOrders);
 *
 * // Or use multiple values
 * const { orders, isLoading, loadOrders } = useFleetHubStore();
 * ```
 */
export const useFleetHubStore = create<FleetHubState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      /**
       * Load fleet orders from Firebase Functions
       * Applies current filter state (always sorts by pickup date)
       */
      loadOrders: async (page = 1) => {
        const requestId = ++_loadOrdersRequestId
        set({ isLoading: true, error: null })

        try {
          // Import dynamically to avoid circular dependencies
          const { getFleetOrders } = await _functionsModuleLoader.load()

          const state = get()

          console.log("[FleetHub] Loading orders with filters:", {
            page,
            filters: state.filters,
            sortBy: "date", // Always sort by pickup date
            sortDirection: state.filters.sortDirection,
          })

          const result = await getFleetOrders({
            page,
            pageSize: state.pageSize,
            sortBy: "date", // Always sort by pickup date (scheduledPickupTime)
            sortDirection: state.filters.sortDirection,
            dateRange: hasActiveDateFilter(state.filters.dateRange)
              ? {
                  type: "custom",
                  startDate: state.filters.dateRange.startDate || undefined,
                  endDate: state.filters.dateRange.endDate || undefined,
                }
              : undefined,
            numberPlate: state.numberPlateFilter || undefined,
          })

          // Discard stale response if a newer request was fired while we awaited
          if (requestId !== _loadOrdersRequestId) return

          // Use backend results directly (no client-side filtering)
          set({
            orders: result.orders,
            currentPage: result.page,
            totalCount: result.totalCount,
            hasMore: result.hasMore,
            isLoading: false,
          })

          // Show password change prompt if user hasn't changed initial password
          if (page === 1 && (result.changedInitialPassword ?? true) === false) {
            set({ changedInitialPassword: false })
            if (!get().passwordPromptDismissed) {
              set({ showPasswordPrompt: true })
            }
          }

          // Sync live tracking with new orders
          get().syncTracking()
        } catch (error: any) {
          console.error("[FleetHub] Error loading orders:", error)

          // PLA-534: the backend's B2B guard refused this session — the account is
          // not, or is no longer, a valid FleetHub customer. Expected per ADR-0020
          // (WARN tier); flagged so the screen can sign out rather than leave the
          // user on a dashboard that can only 403.
          const accessDenied = isB2bAccessDenied(error)

          // unauthenticated is expected after session expiry — callableWithAuthRetry
          // already attempted a token refresh and retried. Capturing it here produces
          // noise; the redirect-to-login path handles the UX.
          const isUnauthenticated =
            error?.code === "functions/unauthenticated" ||
            error?.code === "unauthenticated" ||
            error?.message?.includes("Bitte melde dich an")
          // LOGFIX-PLA-600: still capture on accessDenied — ADR-0036 requires a
          // permission-denied outcome to reach Sentry at WARN, never be dropped.
          // downgradeExpectedThrow (sentryExpectedThrows.ts) already recognizes the
          // B2B-guard code/message added in PLA-534, so this is captured as WARN,
          // not ERROR — no noise reintroduced.
          if (!isUnauthenticated) {
            Sentry.captureException(error, {
              tags: {
                flow: "fleethub",
                operation: "load-orders",
                ...(accessDenied ? { b2bAccessDenied: "true" } : {}),
              },
            })
          }
          set({
            error: error.message || "Fehler beim Laden der Aufträge",
            isLoading: false,
            ...(accessDenied ? { b2bAccessRevoked: true } : {}),
          })
        }
      },

      /**
       * Load map data (customer locations + hub locations)
       */
      loadMapData: async () => {
        set({ isLoadingMapData: true, mapDataError: null })

        try {
          // Import dynamically to avoid circular dependencies
          const { getFleetHubMapData } = await _functionsModuleLoader.load()

          const result = await getFleetHubMapData()

          set({
            customerLocations: result.customerLocations,
            hubLocations: result.hubLocations,
            isLoadingMapData: false,
          })
        } catch (error: any) {
          console.error("[FleetHub] Error loading map data:", error)
          // PLA-534 — same B2B-guard handling as loadOrders.
          const accessDenied = isB2bAccessDenied(error)
          // unauthenticated is expected post session-expiry; suppressed here for the
          // same reason as loadOrders — callableWithAuthRetry already retried once.
          const isUnauthenticated =
            error?.code === "functions/unauthenticated" ||
            error?.code === "unauthenticated" ||
            error?.message?.includes("Bitte melde dich an")
          // LOGFIX-PLA-600: still capture on accessDenied — see loadOrders above.
          if (!isUnauthenticated) {
            Sentry.captureException(error, {
              tags: {
                flow: "fleethub",
                operation: "load-map-data",
                ...(accessDenied ? { b2bAccessDenied: "true" } : {}),
              },
            })
          }
          set({
            mapDataError: error.message || "Fehler beim Laden der Kartendaten",
            isLoadingMapData: false,
            ...(accessDenied ? { b2bAccessRevoked: true } : {}),
          })
        }
      },

      /**
       * Change current page and load orders
       */
      setPage: (page: number) => {
        set({ currentPage: page })
        get().loadOrders(page)
      },

      /**
       * Set filter state (doesn't apply - use applyFilters)
       */
      setFilters: (filters: FilterState) => {
        console.log("[FleetHub] Setting filters:", filters)
        set({ filters })
      },

      /**
       * Reset filters to default state
       */
      resetFilters: () => {
        console.log("[FleetHub] Resetting filters to default")
        set({ filters: defaultFilters, currentPage: 1 })
        get().loadOrders(1)
      },

      /**
       * Apply current filters and reload orders
       */
      applyFilters: async () => {
        console.log("[FleetHub] Applying filters and reloading orders")
        set({ currentPage: 1 })
        await get().loadOrders(1)
      },

      /**
       * Search orders by number plate prefix
       * Sets the plate filter and reloads from page 1
       */
      searchByPlate: (normalizedPlate: string) => {
        console.log("[FleetHub] Searching by plate:", normalizedPlate)
        set({ numberPlateFilter: normalizedPlate, currentPage: 1 })
        get().loadOrders(1)
      },

      /**
       * Clear number plate search and reload all orders
       */
      clearSearch: () => {
        console.log("[FleetHub] Clearing plate search")
        set({ numberPlateFilter: "", currentPage: 1 })
        get().loadOrders(1)
      },

      /**
       * Open reschedule modal for specific order
       */
      openRescheduleModal: (order: FleetOrder) => {
        set({ selectedOrder: order, showRescheduleModal: true })
      },

      /**
       * Close reschedule modal
       */
      closeRescheduleModal: () => {
        set({ showRescheduleModal: false, selectedOrder: null })
      },

      /**
       * Open cancel modal for specific order
       */
      openCancelModal: (order: FleetOrder) => {
        set({ selectedOrder: order, showCancelModal: true })
      },

      /**
       * Close cancel modal
       */
      closeCancelModal: () => {
        set({ showCancelModal: false, selectedOrder: null })
      },

      /**
       * Open booking modal
       */
      openBookingModal: () => {
        set({ showBookingModal: true, bookingNachkontrollePreselected: false, bookingError: null })
      },

      /**
       * Open booking modal with Nachkontrolle pre-selected
       */
      openBookingModalWithNachkontrolle: () => {
        set({ showBookingModal: true, bookingNachkontrollePreselected: true, bookingError: null })
      },

      /**
       * Close booking modal
       */
      closeBookingModal: () => {
        set({ showBookingModal: false, bookingNachkontrollePreselected: false, bookingError: null })
      },

      /**
       * Create a new FleetHub order
       * Shows loading card and polls for new order until it appears or 3 minutes pass
       */
      createFleetOrder: async (data: {
        abholdatum: string
        spatesteAbholung: string
        nachkontrolle: boolean
        walkIn?: boolean
        childCompanyId?: string
        numberPlate?: string
        inspectionType?: import("@/services/firebase/functions").InspectionType
        customerNotes?: string
      }) => {
        set({ isBookingOrder: true, bookingError: null })

        try {
          // Import dynamically to avoid circular dependencies
          const { createFleetHubOrder } = await _functionsModuleLoader.load()

          // Debug logging to see what's being sent to backend
          console.log("[FleetHub] Creating order with params:", {
            abholdatum: data.abholdatum,
            spatesteAbholung: data.spatesteAbholung,
            nachkontrolle: data.nachkontrolle,
            walkIn: data.walkIn,
            numberPlate: data.numberPlate,
            inspectionType: data.inspectionType,
          })

          // Call backend to create HubSpot ticket
          const result = await createFleetHubOrder(data)

          console.log("[FleetHub] Order created successfully", { ticketId: result.ticketId })

          // Close modal
          set({ showBookingModal: false, isBookingOrder: false })

          // Show loading card (ghost card renders once pendingOrderDraft is set
          // by the modal). Start in the "pending" phase. (PLA-433)
          set({ showLoadingCard: true, loadingCardPhase: "pending" })

          // Subscribe to the public `pending-order-{ticketId}` channel BEFORE arming
          // the polling fallback. The backend broadcasts ORDER_CREATED on this channel
          // as soon as syncOrderToFirestore completes (typically <30s), so the new
          // order appears the moment it's queryable instead of waiting for the next
          // poll tick. The polling intervals below remain as a fallback for the case
          // where Pusher delivery is delayed or fails.
          //
          // Dispose any leftover pending pusher from a previous booking that didn't
          // clean up (e.g. user re-booked before the previous order appeared).
          const previousPending = get().pendingOrderPusher
          if (previousPending) {
            try {
              previousPending.disconnect()
            } catch (disposeError) {
              console.warn("[FleetHub] Failed to dispose previous pending pusher", disposeError)
            }
            // A leftover pending pusher means a prior booking never finalized.
            // Cancel any deferred synced-swap from it and reset the ghost phase
            // so the new ghost card starts clean in "pending". (PLA-433)
            const leftoverSyncTimeout = get().loadingCardSyncTimeout
            if (leftoverSyncTimeout) clearTimeout(leftoverSyncTimeout)
            set({ loadingCardPhase: "pending", loadingCardSyncTimeout: null })
          }

          try {
            const pendingPusher = new PusherService()
            pendingPusher.subscribeToPendingOrder(result.ticketId, () => {
              console.log(`[FleetHub] Pusher: pending order ${result.ticketId} signalled ready`)
              // checkForNewOrder will refresh the list, highlight the new order,
              // and call hideLoadingCard — which disposes this pendingPusher.
              get().checkForNewOrder()
            })
            set({ pendingOrderPusher: pendingPusher })
          } catch (subscribeError) {
            // Pre-subscription is best-effort — polling fallback still covers the case.
            console.warn("[FleetHub] Failed to pre-subscribe to pending order:", subscribeError)
            Sentry.captureException(subscribeError, {
              tags: { flow: "fleethub", operation: "pending-order-subscribe" },
            })
          }

          // Set up polling intervals: 30s, 60s, 90s, 120s
          const intervals: NodeJS.Timeout[] = []

          // Poll at 30 seconds
          intervals.push(
            setTimeout(async () => {
              await get().checkForNewOrder()
            }, 30 * 1000),
          )

          // Poll at 60 seconds
          intervals.push(
            setTimeout(async () => {
              await get().checkForNewOrder()
            }, 60 * 1000),
          )

          // Poll at 90 seconds
          intervals.push(
            setTimeout(async () => {
              await get().checkForNewOrder()
            }, 90 * 1000),
          )

          // Poll at 120 seconds
          intervals.push(
            setTimeout(async () => {
              await get().checkForNewOrder()
            }, 120 * 1000),
          )

          // Poll at 150 seconds
          intervals.push(
            setTimeout(async () => {
              await get().checkForNewOrder()
            }, 150 * 1000),
          )

          // Poll at 180 seconds (final check before timeout)
          intervals.push(
            setTimeout(async () => {
              await get().checkForNewOrder()
            }, 180 * 1000),
          )

          // Auto-hide loading card after 3 minutes (failsafe)
          const timeout = setTimeout(
            () => {
              get().hideLoadingCard()
            },
            3 * 60 * 1000,
          )

          set({ loadingCardTimeout: timeout, pollingIntervals: intervals })

          // Initial refresh to potentially show the new order immediately
          await get().checkForNewOrder()
        } catch (error: any) {
          // A server-side booking gate (e.g. Hol- und Bring-Service disabled at
          // this hub, ADR-0051) is a validation-tier rejection, not an outage:
          // its German message is customer-ready and must reach the modal, and
          // per ADR-0036 it is logged at WARN rather than captured as a defect.
          // Everything else keeps the generic "call us" fallback.
          const rejectionMessage = bookingRejectionMessage(error)
          if (rejectionMessage) {
            console.warn("[FleetHub] Booking rejected by server:", error)
            Sentry.captureException(error, {
              level: "warning",
              tags: { flow: "fleethub", operation: "create-order" },
            })
            set({ bookingError: rejectionMessage, isBookingOrder: false })
            return
          }

          console.error("[FleetHub] Error creating order:", error)
          Sentry.captureException(error, {
            tags: { flow: "fleethub", operation: "create-order" },
          })
          set({
            bookingError:
              "Entschuldige, unsere Server sind gerade etwas langsamer. Rufe uns gerne unter: 0911 8808306 an und buche deine Untersuchung.",
            isBookingOrder: false,
          })
        }
      },

      /**
       * Run post-booking side-effects (Pusher, polling, loading card) for a
       * ticketId that has already been created. Called by the document-upload
       * path in BookInspectionModal so the modal can stay open during the attach
       * step. Idempotent: does not close the modal or touch bookingError.
       * (PLA-122 / ADR-0022)
       */
      startPostBookingFlow: async (ticketId: string) => {
        // Ghost card renders once pendingOrderDraft is set by the modal (which
        // it does immediately before calling this). Start in "pending". (PLA-433)
        set({ showLoadingCard: true, loadingCardPhase: "pending" })

        const previousPending = get().pendingOrderPusher
        if (previousPending) {
          try {
            previousPending.disconnect()
          } catch {
            // best-effort
          }
          // Cancel any deferred synced-swap left over from a prior booking so
          // the new ghost card starts clean. (PLA-433)
          const leftoverSyncTimeout = get().loadingCardSyncTimeout
          if (leftoverSyncTimeout) clearTimeout(leftoverSyncTimeout)
          set({ loadingCardSyncTimeout: null })
        }

        try {
          const pendingPusher = new PusherService()
          pendingPusher.subscribeToPendingOrder(ticketId, () => {
            get().checkForNewOrder()
          })
          set({ pendingOrderPusher: pendingPusher })
        } catch (subscribeError) {
          console.warn("[FleetHub] Failed to pre-subscribe to pending order:", subscribeError)
          Sentry.captureException(subscribeError, {
            tags: { flow: "fleethub", operation: "pending-order-subscribe" },
          })
        }

        const intervals: NodeJS.Timeout[] = []
        const pollDelays = [30, 60, 90, 120, 150, 180]
        for (const delaySeconds of pollDelays) {
          intervals.push(setTimeout(() => get().checkForNewOrder(), delaySeconds * 1000))
        }

        const timeout = setTimeout(() => get().hideLoadingCard(), 3 * 60 * 1000)
        set({ loadingCardTimeout: timeout, pollingIntervals: intervals })

        await get().checkForNewOrder()
      },

      /**
       * Reschedule an existing FleetHub order
       */
      rescheduleFleetOrder: async (
        orderId: string,
        data: { abholdatum: string; spatesteAbholung: string },
      ) => {
        set({ isReschedulingOrder: true, rescheduleError: null })

        try {
          // Import dynamically to avoid circular dependencies
          const { rescheduleFleetHubOrder } = await _functionsModuleLoader.load()

          // Call backend to update HubSpot ticket
          await rescheduleFleetHubOrder({ orderId, ...data })

          console.log("[FleetHub] Order rescheduled successfully", { orderId })

          // Close modal
          set({ showRescheduleModal: false, selectedOrder: null })

          // Clear any prior in-flight row action + its timers (single-pending),
          // then arm the per-row pending overlay for this reschedule. (PLA-433)
          _clearPendingRowTimers(get())
          set({
            isReschedulingOrder: false,
            pendingRowAction: {
              orderId,
              type: "reschedule",
              newDate: data.abholdatum,
              newTime: data.spatesteAbholung,
              phase: "pending",
            },
            pendingRowPollInterval: null,
            pendingRowSyncTimeout: null,
            pendingRowFailsafeTimeout: null,
          })

          const originalPickup = get().orders.find((o) => o.id === orderId)?.spatesteAbholung
          const originalScheduled = get().orders.find((o) => o.id === orderId)?.scheduledPickupTime

          // Poll every 3s until the order's pickup time changes
          const pollInterval = setInterval(async () => {
            try {
              await get().loadOrders(get().currentPage)
              // Re-check after the await: a newer row action (single-pending),
              // hidePendingRowAction, or an earlier tick that already flipped to
              // "synced" may have superseded this one in flight.
              const active = get().pendingRowAction
              if (
                !active ||
                active.orderId !== orderId ||
                active.type !== "reschedule" ||
                active.phase !== "pending"
              )
                return

              const updated = get().orders.find((o) => o.id === orderId)
              // Bug fix (PLA-433): guard with `updated &&`. When the user has
              // paginated away, the order is absent from the current page and
              // `undefined !== originalPickup` would false-positive an instant
              // "reschedule detected" before the change actually landed.
              if (
                updated &&
                (updated.spatesteAbholung !== originalPickup ||
                  updated.scheduledPickupTime !== originalScheduled)
              ) {
                console.log("[FleetHub] Reschedule detected, entering synced phase")
                // Stop the poll + cancel the failsafe; play the brand check
                // pulse for ~800ms, then clear the row action + highlight.
                _clearPendingRowTimers(get())
                const current = get().pendingRowAction
                set({
                  pendingRowAction: current ? { ...current, phase: "synced" } : null,
                  pendingRowPollInterval: null,
                  pendingRowFailsafeTimeout: null,
                })
                const syncTimeout = setTimeout(() => {
                  set({ pendingRowAction: null, pendingRowSyncTimeout: null })
                  get().highlightOrder(orderId)
                }, 800)
                set({ pendingRowSyncTimeout: syncTimeout })
              }
            } catch {
              /* silent */
            }
          }, 3000)

          // Failsafe: 30s max — clear everything to null, no pulse.
          const failsafeTimeout = setTimeout(() => {
            _clearPendingRowTimers(get())
            get().loadOrders(get().currentPage)
            set({
              pendingRowAction: null,
              pendingRowPollInterval: null,
              pendingRowSyncTimeout: null,
              pendingRowFailsafeTimeout: null,
            })
          }, 30000)

          set({ pendingRowPollInterval: pollInterval, pendingRowFailsafeTimeout: failsafeTimeout })
        } catch (error: any) {
          console.error("[FleetHub] Error rescheduling order:", error)
          set({
            rescheduleError: error.message || "Fehler beim Verschieben des Auftrags",
            isReschedulingOrder: false,
          })
        }
      },

      /**
       * Cancel an existing FleetHub order
       */
      cancelFleetOrder: async (orderId: string) => {
        set({ isCancellingOrder: true, cancelError: null })

        try {
          // Import dynamically to avoid circular dependencies
          const { cancelFleetHubOrder } = await _functionsModuleLoader.load()

          // Call backend to update HubSpot ticket to cancelled stage
          await cancelFleetHubOrder({ orderId })

          console.log("[FleetHub] Order cancelled successfully", { orderId })

          // Close confirmation modal
          set({ showCancelModal: false, selectedOrder: null })

          // Clear any prior in-flight row action + its timers (single-pending),
          // then arm the per-row pending overlay for this cancellation. (PLA-433)
          _clearPendingRowTimers(get())
          set({
            isCancellingOrder: false,
            pendingRowAction: { orderId, type: "cancel", phase: "pending" },
            pendingRowPollInterval: null,
            pendingRowSyncTimeout: null,
            pendingRowFailsafeTimeout: null,
          })

          // Poll every 3s until the order disappears or is cancelled
          const pollInterval = setInterval(async () => {
            try {
              await get().loadOrders(get().currentPage)
              // Re-check after the await: a newer row action (single-pending),
              // hidePendingRowAction, or an earlier tick that already flipped to
              // "synced" may have superseded this one in flight.
              const active = get().pendingRowAction
              if (
                !active ||
                active.orderId !== orderId ||
                active.type !== "cancel" ||
                active.phase !== "pending"
              )
                return

              const stillExists = get().orders.find((o) => o.id === orderId)
              if (stillExists && stillExists.status === "cancelled") {
                console.log("[FleetHub] Cancellation detected, entering synced phase")
                // Stop the poll + cancel the failsafe; play the brand check
                // pulse for ~800ms, then clear the row action + highlight.
                _clearPendingRowTimers(get())
                const current = get().pendingRowAction
                set({
                  pendingRowAction: current ? { ...current, phase: "synced" } : null,
                  pendingRowPollInterval: null,
                  pendingRowFailsafeTimeout: null,
                })
                const syncTimeout = setTimeout(() => {
                  set({ pendingRowAction: null, pendingRowSyncTimeout: null })
                  get().highlightOrder(orderId)
                }, 800)
                set({ pendingRowSyncTimeout: syncTimeout })
              } else if (!stillExists) {
                // Completion via disappearance: loadOrders already removed the
                // row, so there is no card to attach the pulse/highlight to —
                // clear immediately instead of arming an invisible 800ms dwell.
                console.log(
                  "[FleetHub] Cancellation detected (row removed) — clearing pending action",
                )
                _clearPendingRowTimers(get())
                set({
                  pendingRowAction: null,
                  pendingRowPollInterval: null,
                  pendingRowSyncTimeout: null,
                  pendingRowFailsafeTimeout: null,
                })
              }
            } catch {
              /* silent */
            }
          }, 3000)

          // Failsafe: 30s max — clear everything to null, no pulse.
          const failsafeTimeout = setTimeout(() => {
            _clearPendingRowTimers(get())
            get().loadOrders(get().currentPage)
            set({
              pendingRowAction: null,
              pendingRowPollInterval: null,
              pendingRowSyncTimeout: null,
              pendingRowFailsafeTimeout: null,
            })
          }, 30000)

          set({ pendingRowPollInterval: pollInterval, pendingRowFailsafeTimeout: failsafeTimeout })
        } catch (error: any) {
          console.error("[FleetHub] Error cancelling order:", error)
          set({
            cancelError: error.message || "Fehler beim Stornieren des Auftrags",
            isCancellingOrder: false,
          })
        }
      },

      /**
       * Check if a new order appeared (silent reload)
       * Stops polling if new order is detected
       *
       * Detection is by ID-set difference, not by count. Both
       * `state.orders.length` and `result.orders.length` are capped at
       * `pageSize` (20), so if a tenant already has 20+ orders, a count
       * comparison would always read "20 > 20 = false" and never trip the
       * loading-card hide path — even though the new order is right there in
       * the result, displacing an older one off the visible page.
       */
      checkForNewOrder: async () => {
        const state = get()

        try {
          // Import dynamically to avoid circular dependencies
          const { getFleetOrders } = await _functionsModuleLoader.load()

          // Silently fetch orders (don't show loading state)
          const result = await getFleetOrders({
            page: 1,
            pageSize: state.pageSize,
            sortBy: "date",
            sortDirection: "desc",
          })

          // Re-read state AFTER the await — a racing Pusher event or poll tick
          // may have advanced the phase or swapped the list while we fetched.
          const current = get()

          // During the ~800ms synced dwell the deferred loadingCardSyncTimeout
          // owns the list swap. Any tick that lands here must be a complete
          // no-op, or the ghost card and the real row would briefly coexist.
          if (current.showLoadingCard && current.loadingCardPhase === "synced") {
            console.log("[FleetHub] checkForNewOrder ignored — mid synced dwell")
            return
          }

          // Detect new order by ID set difference (survives pageSize cap).
          // Diff against the fresh post-await orders so a concurrent swap can't
          // re-trigger processing for an order already in the list.
          const oldIds = new Set(current.orders.map((o) => o.id))
          const newOrder = result.orders.find((o) => !oldIds.has(o.id))

          if (newOrder) {
            // Phased ghost handoff (PLA-433): a ghost card is showing → flip to
            // the "synced" phase (freezes the shimmer + plays the brand check
            // pulse) and defer the list swap ~800ms so the pulse is seen before
            // the ghost is replaced by the real (highlighted) row.
            if (current.showLoadingCard && current.loadingCardPhase === "pending") {
              console.log("[FleetHub] New order detected — entering synced phase", {
                newOrderId: newOrder.id,
              })
              // Fingerprint the list query at detection time. setFilters /
              // resetFilters replace the filters object (reference equality),
              // numberPlateFilter covers the plate search (it lives outside
              // filters), and _loadOrdersRequestId catches ANY loadOrders that
              // completes during the dwell — including a pull-to-refresh whose
              // query state is otherwise identical.
              const capturedPage = current.currentPage
              const capturedFilters = current.filters
              const capturedPlate = current.numberPlateFilter
              const capturedRequestId = _loadOrdersRequestId
              const syncTimeout = setTimeout(() => {
                // Re-read after the dwell — a dismiss (hideLoadingCard) would
                // have cleared this timeout, so if we run the card is still up.
                const later = get()
                const queryUnchanged =
                  later.currentPage === capturedPage &&
                  later.filters === capturedFilters &&
                  later.numberPlateFilter === capturedPlate &&
                  _loadOrdersRequestId === capturedRequestId
                if (queryUnchanged) {
                  set({
                    orders: result.orders,
                    totalCount: result.totalCount,
                    hasMore: result.hasMore,
                  })
                } else {
                  // The user paginated/filtered/searched/refreshed during the
                  // dwell — their query is the source of truth now; refresh it
                  // instead of overwriting with the stale page-1 snapshot.
                  later.loadOrders(later.currentPage)
                }
                later.syncTracking()
                later.highlightOrder(newOrder.id)
                later.hideLoadingCard()
              }, 800)
              set({ loadingCardPhase: "synced", loadingCardSyncTimeout: syncTimeout })
              return
            }

            // No ghost card showing (order surfaced via a background poll with
            // no active booking) — legacy immediate swap.
            console.log("[FleetHub] New order detected (no loading card) — immediate swap", {
              newOrderId: newOrder.id,
            })
            set({
              orders: result.orders,
              totalCount: result.totalCount,
              hasMore: result.hasMore,
            })
            current.syncTracking()
            current.highlightOrder(newOrder.id)
            current.hideLoadingCard()
          } else {
            // No new order yet - just update the orders silently
            console.log("[FleetHub] No new order yet, continuing to poll...")
            set({
              orders: result.orders,
              totalCount: result.totalCount,
              hasMore: result.hasMore,
            })

            // Sync tracking in case any order stages changed
            get().syncTracking()
          }
        } catch (error: any) {
          console.error("[FleetHub] Error checking for new order:", error)
          Sentry.captureException(error, {
            tags: { flow: "fleethub", operation: "check-new-order" },
          })
          // Don't show error to user during silent polling
        }
      },

      /**
       * Hide loading card manually
       * Also clears all polling intervals and tears down the pending-order Pusher
       * subscription armed by createFleetOrder.
       *
       * Idempotent: returns early if the loading card is already hidden, so
       * concurrent triggers (Pusher event + polling timer racing on the same tick)
       * don't double-disconnect the pending pusher or repeat cleanup work.
       */
      hideLoadingCard: () => {
        const state = get()

        if (!state.showLoadingCard) {
          return
        }

        // Clear timeout if it exists
        if (state.loadingCardTimeout) {
          clearTimeout(state.loadingCardTimeout)
        }

        // Clear the deferred synced-swap timer (PLA-433). If a dismiss lands
        // during the ~800ms synced dwell this cancels the pending list swap, so
        // the new order simply appears on the next natural refresh.
        if (state.loadingCardSyncTimeout) {
          clearTimeout(state.loadingCardSyncTimeout)
        }

        // Clear all polling intervals
        state.pollingIntervals.forEach((interval) => clearTimeout(interval))

        // Tear down pending-order Pusher subscription (if armed by createFleetOrder)
        if (state.pendingOrderPusher) {
          try {
            state.pendingOrderPusher.disconnect()
          } catch (error) {
            console.warn("[FleetHub] Failed to disconnect pendingOrderPusher", error)
          }
        }

        console.log("[FleetHub] Loading card hidden, polling stopped.")

        set({
          showLoadingCard: false,
          loadingCardTimeout: null,
          loadingCardSyncTimeout: null,
          pollingIntervals: [],
          pendingOrderPusher: null,
          pendingOrderDraft: null,
          loadingCardPhase: "pending",
        })
      },

      /**
       * Store the draft of a booking-in-flight so the ghost card can render the
       * known fields while the sync completes. Set by BookInspectionModal
       * immediately before the booking call; cleared by hideLoadingCard. (PLA-433)
       */
      setPendingOrderDraft: (draft: PendingOrderDraft) => {
        set({ pendingOrderDraft: draft })
      },

      /**
       * Clear the in-flight row action (cancel/reschedule) and all three of its
       * timers. Used by the ghost overlay's dismiss affordance and as a manual
       * teardown; safe to call when nothing is pending. (PLA-433)
       */
      hidePendingRowAction: () => {
        _clearPendingRowTimers(get())

        console.log("[FleetHub] Pending row action cleared.")

        set({
          pendingRowAction: null,
          pendingRowPollInterval: null,
          pendingRowSyncTimeout: null,
          pendingRowFailsafeTimeout: null,
        })
      },

      /**
       * Highlight an order card with a glow animation (auto-clears after 2s)
       */
      highlightOrder: (orderId: string) => {
        set({ highlightedOrderId: orderId })
        setTimeout(() => set({ highlightedOrderId: null }), 2000)
      },

      /**
       * Reset store to initial state
       */
      reset: () => {
        const state = get()

        // Clear loading card timeout if it exists
        if (state.loadingCardTimeout) {
          clearTimeout(state.loadingCardTimeout)
        }

        // Clear the PLA-433 sync/row-action timers (set(initialState) below
        // zeroes the fields; these clearTimeout/clearInterval calls stop the
        // underlying OS timers so no deferred swap fires post-reset).
        if (state.loadingCardSyncTimeout) clearTimeout(state.loadingCardSyncTimeout)
        _clearPendingRowTimers(state)

        // Clear all polling intervals
        state.pollingIntervals.forEach((interval) => clearTimeout(interval))

        // Tear down pending-order Pusher subscription if armed
        if (state.pendingOrderPusher) {
          try {
            state.pendingOrderPusher.disconnect()
          } catch (error) {
            console.warn("[FleetHub] Failed to disconnect pendingOrderPusher in reset", error)
          }
        }

        set(initialState)
      },

      /**
       * Check if an order is active (not completed, not no_show, not cancelled)
       */
      isOrderActive: (order: FleetOrder): boolean => {
        // Exclude completed orders
        if (order.status === "completed") return false

        // Exclude no_show
        if (order.status === "no_show" || order.status === "709823443") return false

        // Exclude cancelled (explicit checks)
        if (order.status === "cancelled" || order.status === "3552907480") return false

        // Must have tracking token
        if (!order.trackingToken) return false

        return true
      },

      /**
       * Check if an order needs live tracking (active and in live tracking phase)
       */
      needsLiveTracking: (order: FleetOrder): boolean => {
        if (!get().isOrderActive(order)) return false

        // Status to numeric phase mapping (from Order.ts)
        const STATUS_TO_NUMERIC_PHASE: { [key: string]: number } = {
          new: 1,
          confirmed: 2,
          pickup_due: 2,
          picking_up: 3,
          at_hub: 4,
          ready_to_return: 5,
          returning: 6,
          completed: 7,
          no_show: 1,
          backlog: 1,
          cancelled: 1,
        }

        // Only show live driver tracking for pickup/return phases
        const phase = STATUS_TO_NUMERIC_PHASE[order.status] || 0
        return phase === 3 || phase === 6 // picking_up or returning
      },

      /**
       * Initialize live tracking for all active orders with error handling
       */
      initializeLiveTracking: async () => {
        console.log("[FleetHub] Initializing live tracking")

        const state = get()

        // Clear any previous errors
        set({ trackingError: null, failedOrderSubscriptions: [] })

        try {
          // Stop any existing tracking
          state.stopLiveTracking()

          // Validate we have orders loaded
          if (state.orders.length === 0) {
            console.warn("[FleetHub] No orders loaded, skipping live tracking initialization")
            return
          }

          // Create new Pusher service
          const pusherService = new MultiOrderPusherService()
          set({ pusherService, isTrackingActive: true })

          // Get all active orders to subscribe to Pusher
          // NOTE: We subscribe to ALL active orders (not just live tracking phases)
          // so we can receive stage updates for transitions like 2→3, 5→6, etc.
          const ordersToTrack = state.orders.filter((order) => {
            try {
              return state.isOrderActive(order)
            } catch (error) {
              console.error(`[FleetHub] Error checking if order ${order.id} is active:`, error)
              return false
            }
          })

          console.log(`[FleetHub] Found ${ordersToTrack.length} active orders to track via Pusher`)

          if (ordersToTrack.length === 0) {
            console.log("[FleetHub] No active orders to track")
            set({ isTrackingActive: false, pusherService: null })
            return
          }

          const failedSubscriptions: string[] = []

          // Build activeOrders Map outside loop to avoid state update race conditions
          const newActiveOrders = new Map<string, ActiveOrderTrackingData>()

          // Subscribe to each order's Pusher channel
          ordersToTrack.forEach((order) => {
            // Validate trackingToken
            if (!order.trackingToken) {
              console.warn(`[FleetHub] Order ${order.id} missing trackingToken, skipping`)
              failedSubscriptions.push(order.id)
              return
            }

            // Validate order ID
            if (!order.id) {
              console.error(`[FleetHub] Order missing ID, skipping`)
              return
            }

            console.log(`[FleetHub] Subscribing to order ${order.id}`)

            try {
              // Find hub location for this order
              let hubLocation: HubLocation | null = null

              // Try to use order's hubLocation if provided by backend
              if (order.hubLocation) {
                hubLocation = {
                  id: order.hubLocation.id,
                  location: order.hubLocation.name,
                  address: order.hubLocation.address,
                  latitude: order.hubLocation.latitude,
                  longitude: order.hubLocation.longitude,
                  city: "", // Not needed for display
                }
              } else if (order.zustandigePrufstelle && state.hubLocations.length > 0) {
                // Fallback: Match inspection center name to hub locations
                hubLocation =
                  state.hubLocations.find((hub) => hub.location === order.zustandigePrufstelle) ||
                  null
              }

              // Determine customer location for this order
              let customerLoc: { latitude: number; longitude: number } | null = null

              // Use order-specific customer location if available
              if (order.customerLocation) {
                customerLoc = {
                  latitude: order.customerLocation.latitude,
                  longitude: order.customerLocation.longitude,
                }
              } else if (state.customerLocations.length > 0) {
                // Fallback to first B2B customer office location
                customerLoc = {
                  latitude: state.customerLocations[0].latitude,
                  longitude: state.customerLocations[0].longitude,
                }
              }

              // Initialize tracking data for this order
              const trackingData: ActiveOrderTrackingData = {
                orderId: order.id,
                status: order.status,
                serviceType: order.serviceType,
                // Seed the last-known position from the persisted tracking
                // mirror (via getFleetOrders) so the car paints on reload before
                // the first live Pusher tick. Live updates overwrite it.
                driverLocation: order.currentDriverLocation
                  ? {
                      latitude: order.currentDriverLocation.latitude,
                      longitude: order.currentDriverLocation.longitude,
                      timestamp: order.currentDriverLocation.timestamp,
                      isReturn: order.currentDriverLocation.isReturn,
                    }
                  : null,
                hubLocation,
                customerLocation: customerLoc,
                orderPhase: STATUS_TO_NUMERIC_PHASE[order.status] || 0,
                numberPlate: order.numberPlate,
                customerName: order.customerName,
                customerAddress: order.customerLocation?.address,
              }

              // Add to active orders Map (built outside loop)
              newActiveOrders.set(order.id, trackingData)

              // Subscribe to Pusher channel with error-wrapped handlers
              pusherService.subscribeToOrder(order.id, order.trackingToken, {
                onLocationUpdate: (data: LocationUpdate) => {
                  try {
                    console.log(`[FleetHub] 📍 Location update for order ${order.id}:`, {
                      lat: data.latitude,
                      lng: data.longitude,
                      isReturn: data.isReturn,
                      numberPlate: order.numberPlate,
                    })

                    // Validate location data
                    if (!data.latitude || !data.longitude) {
                      console.warn(`[FleetHub] Invalid location data for order ${order.id}`)
                      return
                    }

                    state.updateDriverLocation(order.id, {
                      latitude: data.latitude,
                      longitude: data.longitude,
                      timestamp: data.timestamp,
                      isReturn: data.isReturn,
                    })
                  } catch (error) {
                    console.error(
                      `[FleetHub] Error handling location update for order ${order.id}:`,
                      error,
                    )
                  }
                },
                onStageUpdate: (data: StageUpdate) => {
                  try {
                    console.log(`[FleetHub] Stage update for order ${order.id}`, data)

                    if (!data.newStage) {
                      console.warn(`[FleetHub] Invalid stage data for order ${order.id}`)
                      return
                    }

                    state.updateOrderStatus(order.id, data.newStage)

                    // PLA-112: belt-and-suspenders refetch. The stage-update
                    // payload is minimal — fields like berichtPdfStatus and
                    // inspectionPassed are not on the wire, so a stale page
                    // would never pick them up if the dedicated Pusher event
                    // is dropped. Mirrors silentRefreshOrderData in /track.
                    get()
                      .loadOrders(get().currentPage)
                      .catch(() => {
                        // Silent — refetch failures must not disrupt the live UI.
                      })
                  } catch (error) {
                    console.error(
                      `[FleetHub] Error handling stage update for order ${order.id}:`,
                      error,
                    )
                  }
                },
                onDriverStarted: (data: DriverStatusUpdate) => {
                  console.log(`[FleetHub] Driver started for order ${order.id}`, data)
                  // If the "started" event includes initial GPS position, update
                  // the driver location so the car appears on the map immediately
                  const dataAny = data as any
                  if (
                    typeof dataAny.latitude === "number" &&
                    typeof dataAny.longitude === "number"
                  ) {
                    state.updateDriverLocation(order.id, {
                      latitude: dataAny.latitude,
                      longitude: dataAny.longitude,
                      timestamp: data.timestamp || Date.now(),
                      isReturn: data.isReturn ?? false,
                    })
                  }
                },
                onDriverArrived: (data: DriverStatusUpdate) => {
                  console.log(`[FleetHub] Driver arrived for order ${order.id}`, data)
                },
                onDriverCancelled: (data: DriverStatusUpdate) => {
                  console.log(`[FleetHub] Driver cancelled for order ${order.id}`, data)
                },
                onETAUpdate: (data: LiveETAUpdate) => {
                  try {
                    // Drop only fully empty payloads. Pickup-only fan-outs
                    // from the backend heartbeat carry projectedPickupAtLive
                    // without liveMinutes — those must still propagate so the
                    // displayed pickup time advances during GPS silence.
                    if (
                      data.liveMinutes == null &&
                      data.projectedCompletionAtLive == null &&
                      data.projectedPickupAtLive == null
                    ) {
                      return
                    }
                    state.updateOrderLiveETA(order.id, {
                      liveMinutes: data.liveMinutes,
                      // Never fall back to data.eta — that would defeat the
                      // backend live_return_eta kill switch by synthesising
                      // the projected field from the legacy eta field.
                      projectedCompletionAtLive: data.projectedCompletionAtLive,
                      // ADR-0012 §5: pickup-side projection. Same kill-switch
                      // rule via live_pickup_eta — absent on the wire means
                      // no update; never synthesise.
                      projectedPickupAtLive: data.projectedPickupAtLive,
                    })
                  } catch (error) {
                    console.error(
                      `[FleetHub] Error handling ETA update for order ${order.id}:`,
                      error,
                    )
                  }
                },
                onNumberPlateUpdate: (data: NumberPlateUpdate) => {
                  try {
                    console.log(`[FleetHub] Number plate update for order ${order.id}`, data)
                    if (data.numberPlate !== undefined) {
                      state.updateOrderNumberPlate(order.id, data.numberPlate)
                    }
                  } catch (error) {
                    console.error(
                      `[FleetHub] Error handling number plate update for order ${order.id}:`,
                      error,
                    )
                  }
                },
                onPickupRealized: (data) => {
                  try {
                    const iso = new Date(data.realisedPickupAt).toISOString()
                    state.updateOrderRealisedPickup(order.id, iso)
                  } catch (error) {
                    console.error(
                      `[FleetHub] Error handling pickup-realized for order ${order.id}:`,
                      error,
                    )
                  }
                },
                onReportAvailable: () => {
                  // PLA-111: backend Pusher REPORT_AVAILABLE fans out when the
                  // order's berichtPdfStatus flips to "available". FleetOrderRow
                  // observes that field, so the download button enables on the
                  // next render without a page reload.
                  try {
                    state.markOrderReportAvailable(order.id)
                  } catch (error) {
                    console.error(
                      `[FleetHub] Error handling report-available for order ${order.id}:`,
                      error,
                    )
                  }
                },
              })
            } catch (error) {
              console.error(`[FleetHub] Error subscribing to order ${order.id}:`, error)
              failedSubscriptions.push(order.id)
            }
          })

          // Update state with all active orders atomically (after loop completes)
          set({ activeOrders: newActiveOrders })

          console.log(`[FleetHub] Initialized tracking for ${newActiveOrders.size} orders`)
          console.log("[FleetHub] Active orders:", Array.from(newActiveOrders.keys()))

          // Update failed subscriptions
          if (failedSubscriptions.length > 0) {
            console.warn(
              `[FleetHub] ${failedSubscriptions.length} orders failed to subscribe:`,
              failedSubscriptions,
            )
            set({
              failedOrderSubscriptions: failedSubscriptions,
              trackingError: `${failedSubscriptions.length} order(s) failed to initialize tracking`,
            })
          }

          console.log("[FleetHub] Live tracking initialization complete")
        } catch (error) {
          console.error("[FleetHub] Critical error initializing live tracking:", error)
          Sentry.captureException(error, {
            tags: { flow: "fleethub", operation: "live-tracking-init" },
          })

          // Set error state
          set({
            trackingError:
              error instanceof Error ? error.message : "Failed to initialize live tracking",
            isTrackingActive: false,
            pusherService: null,
          })

          // Clean up any partial state
          state.stopLiveTracking()
        }
      },

      /**
       * Stop live tracking and cleanup all Pusher connections with error handling
       */
      stopLiveTracking: () => {
        console.log("[FleetHub] Stopping live tracking")

        const state = get()

        try {
          if (state.pusherService) {
            state.pusherService.unsubscribeAll()
          }
        } catch (error) {
          console.error("[FleetHub] Error during stopLiveTracking cleanup:", error)
        } finally {
          // Always reset state
          set({
            pusherService: null,
            isTrackingActive: false,
            activeOrders: new Map(),
            trackingError: null,
            failedOrderSubscriptions: [],
          })
        }
      },

      /**
       * Add a single order to live tracking incrementally
       * This allows adding new orders without tearing down existing subscriptions
       *
       * @param order - The order to add to tracking
       */
      addOrderToTracking: (order: FleetOrder) => {
        const state = get()

        // Only add if tracking is active
        if (!state.isTrackingActive || !state.pusherService) {
          console.log(`[FleetHub] Tracking not active, skipping add for order ${order.id}`)
          return
        }

        // Check if already tracking
        if (state.activeOrders.has(order.id)) {
          console.log(`[FleetHub] Order ${order.id} already being tracked`)
          return
        }

        // Subscribe to every active order (matches initializeLiveTracking) so we
        // receive stage transitions like confirmed→picking_up and
        // ready_to_return→returning live. `order.stage` (numeric) is not
        // populated by the backend, so an `order.stage ∈ {2,3,5}` filter here
        // would always bail and re-syncs would silently drop the subscription.
        if (!state.isOrderActive(order)) {
          console.log(`[FleetHub] Order ${order.id} not active, skipping subscribe`)
          return
        }

        // Validate tracking token
        if (!order.trackingToken) {
          console.warn(`[FleetHub] Order ${order.id} missing trackingToken, cannot track`)
          return
        }

        console.log(`[FleetHub] Adding order ${order.id} to live tracking`)

        try {
          const pusherService = state.pusherService

          // Find hub location for this order
          let hubLocation: HubLocation | null = null

          if (order.hubLocation) {
            hubLocation = {
              id: order.hubLocation.id,
              location: order.hubLocation.name,
              address: order.hubLocation.address,
              latitude: order.hubLocation.latitude,
              longitude: order.hubLocation.longitude,
              city: "",
            }
          } else if (order.zustandigePrufstelle && state.hubLocations.length > 0) {
            hubLocation =
              state.hubLocations.find((hub) => hub.location === order.zustandigePrufstelle) || null
          }

          // Determine customer location for this order
          let customerLoc: { latitude: number; longitude: number } | null = null

          if (order.customerLocation) {
            customerLoc = {
              latitude: order.customerLocation.latitude,
              longitude: order.customerLocation.longitude,
            }
          } else if (state.customerLocations.length > 0) {
            customerLoc = {
              latitude: state.customerLocations[0].latitude,
              longitude: state.customerLocations[0].longitude,
            }
          }

          // Initialize tracking data for this order
          const trackingData: ActiveOrderTrackingData = {
            orderId: order.id,
            status: order.status,
            serviceType: order.serviceType,
            driverLocation: null,
            hubLocation,
            customerLocation: customerLoc,
            orderPhase: STATUS_TO_NUMERIC_PHASE[order.status] || 0,
            numberPlate: order.numberPlate,
            customerName: order.customerName,
            customerAddress: order.customerLocation?.address,
          }

          // Add to active orders Map
          const newActiveOrders = new Map(state.activeOrders)
          newActiveOrders.set(order.id, trackingData)
          set({ activeOrders: newActiveOrders })

          // Subscribe to Pusher channel
          pusherService.subscribeToOrder(order.id, order.trackingToken, {
            onLocationUpdate: (data: LocationUpdate) => {
              try {
                if (!data.latitude || !data.longitude) {
                  console.warn(`[FleetHub] Invalid location data for order ${order.id}`)
                  return
                }
                state.updateDriverLocation(order.id, {
                  latitude: data.latitude,
                  longitude: data.longitude,
                  timestamp: data.timestamp,
                  isReturn: data.isReturn,
                })
              } catch (error) {
                console.error(
                  `[FleetHub] Error handling location update for order ${order.id}:`,
                  error,
                )
              }
            },
            onStageUpdate: (data: StageUpdate) => {
              try {
                if (!data.newStage) {
                  console.warn(`[FleetHub] Invalid stage data for order ${order.id}`)
                  return
                }
                state.updateOrderStatus(order.id, data.newStage)

                // PLA-112: belt-and-suspenders refetch. The stage-update
                // payload doesn't carry berichtPdfStatus / inspectionPassed,
                // so a dropped REPORT_AVAILABLE event would otherwise leave
                // the page stale. Mirrors silentRefreshOrderData in /track.
                get()
                  .loadOrders(get().currentPage)
                  .catch(() => {
                    // Silent — refetch failures must not disrupt the live UI.
                  })
              } catch (error) {
                console.error(
                  `[FleetHub] Error handling stage update for order ${order.id}:`,
                  error,
                )
              }
            },
            onDriverStarted: (data: DriverStatusUpdate) => {
              console.log(`[FleetHub] Driver started for order ${order.id}`, data)
              // If the "started" event includes initial GPS, show car immediately
              const dataAny = data as any
              if (typeof dataAny.latitude === "number" && typeof dataAny.longitude === "number") {
                state.updateDriverLocation(order.id, {
                  latitude: dataAny.latitude,
                  longitude: dataAny.longitude,
                  timestamp: data.timestamp || Date.now(),
                  isReturn: data.isReturn ?? false,
                })
              }
            },
            onDriverArrived: (data: DriverStatusUpdate) => {
              console.log(`[FleetHub] Driver arrived for order ${order.id}`, data)
            },
            onDriverCancelled: (data: DriverStatusUpdate) => {
              console.log(`[FleetHub] Driver cancelled for order ${order.id}`, data)
            },
            onETAUpdate: (data: LiveETAUpdate) => {
              try {
                // Drop only fully empty payloads. Pickup-only fan-outs from
                // the backend heartbeat carry projectedPickupAtLive without
                // liveMinutes — those must still propagate so the displayed
                // pickup time advances during GPS silence.
                if (
                  data.liveMinutes == null &&
                  data.projectedCompletionAtLive == null &&
                  data.projectedPickupAtLive == null
                ) {
                  return
                }
                const state = get()
                state.updateOrderLiveETA(order.id, {
                  liveMinutes: data.liveMinutes,
                  // Never fall back to data.eta — that would defeat the
                  // backend live_return_eta kill switch by synthesising
                  // the projected field from the legacy eta field.
                  projectedCompletionAtLive: data.projectedCompletionAtLive,
                  // ADR-0012 §5: pickup-side projection. Same kill-switch
                  // rule via live_pickup_eta — absent on the wire means
                  // no update; never synthesise.
                  projectedPickupAtLive: data.projectedPickupAtLive,
                })
              } catch (error) {
                console.error(`[FleetHub] Error handling ETA update for order ${order.id}:`, error)
              }
            },
            onNumberPlateUpdate: (data: NumberPlateUpdate) => {
              try {
                console.log(`[FleetHub] Number plate update for order ${order.id}`, data)
                if (data.numberPlate !== undefined) {
                  state.updateOrderNumberPlate(order.id, data.numberPlate)
                }
              } catch (error) {
                console.error(
                  `[FleetHub] Error handling number plate update for order ${order.id}:`,
                  error,
                )
              }
            },
            onPickupRealized: (data) => {
              try {
                const iso = new Date(data.realisedPickupAt).toISOString()
                state.updateOrderRealisedPickup(order.id, iso)
              } catch (error) {
                console.error(
                  `[FleetHub] Error handling pickup-realized for order ${order.id}:`,
                  error,
                )
              }
            },
            onReportAvailable: () => {
              // PLA-111: see comment in initializeLiveTracking subscribeToOrder
              // call. The bottom-sheet / FleetOrderRow observe berichtPdfStatus
              // directly — flipping the field on the order in state is enough.
              try {
                state.markOrderReportAvailable(order.id)
              } catch (error) {
                console.error(
                  `[FleetHub] Error handling report-available for order ${order.id}:`,
                  error,
                )
              }
            },
          })

          console.log(`[FleetHub] Successfully added order ${order.id} to tracking`)
        } catch (error) {
          console.error(`[FleetHub] Error adding order ${order.id} to tracking:`, error)
        }
      },

      /**
       * Remove a single order from live tracking
       *
       * @param orderId - The order ID to remove from tracking
       */
      removeOrderFromTracking: (orderId: string) => {
        const state = get()

        if (!state.pusherService) {
          return
        }

        if (!state.activeOrders.has(orderId)) {
          return
        }

        console.log(`[FleetHub] Removing order ${orderId} from live tracking`)

        try {
          // Unsubscribe from Pusher
          state.pusherService.unsubscribeFromOrder(orderId)

          // Remove from activeOrders Map
          const newActiveOrders = new Map(state.activeOrders)
          newActiveOrders.delete(orderId)
          set({ activeOrders: newActiveOrders })
        } catch (error) {
          console.error(`[FleetHub] Error removing order ${orderId} from tracking:`, error)
        }
      },

      /**
       * Sync live tracking with current orders array
       * Adds new orders and removes stale orders without tearing down all connections
       */
      syncTracking: () => {
        const state = get()

        if (!state.isTrackingActive) {
          console.log("[FleetHub] Tracking not active, skipping sync")
          return
        }

        console.log("[FleetHub] Syncing live tracking with current orders")

        const currentOrderIds = new Set(state.orders.map((o) => o.id))
        const trackedOrderIds = new Set(state.activeOrders.keys())

        // Add new orders that aren't being tracked yet
        state.orders.forEach((order) => {
          if (!trackedOrderIds.has(order.id)) {
            state.addOrderToTracking(order)
          }
        })

        // Remove orders that are no longer in the orders array
        trackedOrderIds.forEach((orderId) => {
          if (!currentOrderIds.has(orderId)) {
            state.removeOrderFromTracking(orderId)
          }
        })

        console.log(
          `[FleetHub] Sync complete. Tracking ${state.activeOrders.size} orders out of ${state.orders.length} total`,
        )
      },

      /**
       * Update driver location for a specific order with validation
       */
      updateDriverLocation: (orderId: string, location: DriverLocationData) => {
        try {
          // Validate inputs
          if (!orderId) {
            console.error("[FleetHub] updateDriverLocation called with invalid orderId")
            return
          }

          if (
            !location ||
            typeof location.latitude !== "number" ||
            typeof location.longitude !== "number"
          ) {
            console.error(`[FleetHub] Invalid location data for order ${orderId}:`, location)
            return
          }

          const state = get()
          const orderData = state.activeOrders.get(orderId)

          if (!orderData) {
            console.warn(`[FleetHub] Cannot update location for unknown order ${orderId}`)
            return
          }

          // Update the tracking data with new location
          const updatedData: ActiveOrderTrackingData = {
            ...orderData,
            driverLocation: location,
          }

          const newActiveOrders = new Map(state.activeOrders)
          newActiveOrders.set(orderId, updatedData)
          set({ activeOrders: newActiveOrders })
        } catch (error) {
          console.error(`[FleetHub] Error updating driver location for order ${orderId}:`, error)
        }
      },

      /**
       * Update order status and check if still needs tracking with error handling
       *
       * IMPORTANT: Always updates main orders array (for bottom sheet UI)
       * Optionally updates activeOrders Map (for live tracking visualization)
       */
      updateOrderStatus: (orderId: string, status: string) => {
        try {
          // Validate inputs
          if (!orderId || !status) {
            console.error("[FleetHub] updateOrderStatus called with invalid parameters")
            return
          }

          // Normalise the incoming stage to the English status key used
          // throughout the store and FleetOrderRow's phase map. Pusher
          // stage-update payloads usually carry the English key (e.g.
          // "picking_up"), but the backend falls back to the raw HubSpot
          // stage ID when the value isn't in ALL_STAGES — so a defensive
          // mapping for the no_show / cancelled IDs matches what
          // OrderTrackingStore.updateOrderStatus does on /track.
          let normalizedStatus: string = status
          if (status === "709823443") {
            normalizedStatus = "no_show"
          } else if (status === "3552907480") {
            normalizedStatus = "cancelled"
          } else if (/^\d+$/.test(status)) {
            // Plain numeric stage 1..6 (legacy / fallback path)
            const numericToStatus: Record<string, string> = {
              "1": "new",
              "2": "confirmed",
              "3": "picking_up",
              "4": "at_hub",
              "5": "returning",
              "6": "completed",
            }
            normalizedStatus = numericToStatus[status] ?? status
          }

          const state = get()

          // STEP 1: Always update main orders array first (for bottom sheet UI)
          const updatedOrders = state.orders.map((order) =>
            order.id === orderId ? { ...order, status: normalizedStatus } : order,
          )

          // STEP 2: Check if order is in activeOrders Map (live tracking data)
          const orderData = state.activeOrders.get(orderId)

          if (!orderData) {
            // Order not in activeOrders Map - just update orders array and we're done
            console.log(
              `[FleetHub] Order ${orderId} not in active tracking, updating orders array only`,
            )
            set({ orders: updatedOrders })
            return
          }

          // STEP 3: Order IS in activeOrders Map - update both activeOrders and orders array

          // Status to numeric phase mapping
          const STATUS_TO_NUMERIC_PHASE: { [key: string]: number } = {
            new: 1,
            confirmed: 2,
            pickup_due: 2,
            picking_up: 3,
            at_hub: 4,
            ready_to_return: 5,
            returning: 6,
            completed: 7,
            no_show: 1,
            backlog: 1,
            cancelled: 1,
          }

          // Update status and phase in tracking data
          const updatedData: ActiveOrderTrackingData = {
            ...orderData,
            status: normalizedStatus,
            orderPhase: STATUS_TO_NUMERIC_PHASE[normalizedStatus] || 0,
          }

          // Check if order should still be tracked
          const shouldStillTrack =
            normalizedStatus !== "completed" &&
            normalizedStatus !== "no_show" &&
            normalizedStatus !== "cancelled"

          if (!shouldStillTrack) {
            // Unsubscribe from this order
            console.log(`[FleetHub] Order ${orderId} no longer needs tracking, unsubscribing`)

            try {
              if (state.pusherService) {
                state.pusherService.unsubscribeFromOrder(orderId)
              }
            } catch (error) {
              console.error(`[FleetHub] Error unsubscribing from order ${orderId}:`, error)
            }

            const newActiveOrders = new Map(state.activeOrders)
            newActiveOrders.delete(orderId)
            set({ activeOrders: newActiveOrders, orders: updatedOrders })
          } else {
            // Update tracking data in activeOrders Map
            const newActiveOrders = new Map(state.activeOrders)
            newActiveOrders.set(orderId, updatedData)
            set({ activeOrders: newActiveOrders, orders: updatedOrders })
          }
        } catch (error) {
          console.error(`[FleetHub] Error updating order status for order ${orderId}:`, error)
        }
      },

      /**
       * Update number plate for a specific order
       */
      /**
       * Initialize onboarding tutorial if not already completed
       */
      initializeTutorial: () => {
        if (!isTutorialDone()) {
          set({ showTutorial: true, currentTutorialStep: 0 })
        }
      },

      /**
       * Advance to next tutorial step, or finish if on last step
       */
      nextTutorialStep: () => {
        const { currentTutorialStep } = get()
        if (currentTutorialStep >= 2) {
          // Last step (0-indexed: 0, 1, 2) — mark done
          markTutorialDone()
          set({ showTutorial: false, currentTutorialStep: 0 })
        } else {
          set({ currentTutorialStep: currentTutorialStep + 1 })
        }
      },

      /**
       * Dismiss tutorial permanently
       */
      dismissTutorial: () => {
        markTutorialDone()
        set({ showTutorial: false, currentTutorialStep: 0 })
      },

      /**
       * Update number plate for a specific order
       */
      updateOrderNumberPlate: (orderId: string, numberPlate: string) => {
        try {
          if (!orderId) {
            console.error("[FleetHub] updateOrderNumberPlate called with invalid orderId")
            return
          }

          const state = get()

          // Update main orders array
          const updatedOrders = state.orders.map((order) =>
            order.id === orderId ? { ...order, numberPlate } : order,
          )

          // Update activeOrders Map if order is being tracked
          const orderData = state.activeOrders.get(orderId)
          if (orderData) {
            const updatedData = { ...orderData, numberPlate }
            const newActiveOrders = new Map(state.activeOrders)
            newActiveOrders.set(orderId, updatedData)
            set({ activeOrders: newActiveOrders, orders: updatedOrders })
          } else {
            set({ orders: updatedOrders })
          }

          console.log(`[FleetHub] Number plate updated for order ${orderId}: ${numberPlate}`)
        } catch (error) {
          console.error(`[FleetHub] Error updating number plate for order ${orderId}:`, error)
        }
      },

      /**
       * Update Live ETA fields for an order from a Pusher ETA_UPDATE event.
       * Mutates only the order in the orders array; activeOrders does not
       * carry ETA fields, so it is left untouched.
       */
      updateOrderLiveETA: (orderId, update) => {
        if (!orderId) return
        const state = get()
        const updatedOrders = state.orders.map((order) => {
          if (order.id !== orderId) return order
          const next: FleetOrder = { ...order }
          // Pickup-only Pusher fan-outs (heartbeat during `picking_up`) omit
          // liveMinutes — preserve the prior return-leg value rather than
          // clobbering it with undefined.
          if (update.liveMinutes !== undefined) {
            next.liveEstimatedCompletionMinutes = update.liveMinutes
          }
          // Only overwrite projectedCompletionAtLive when the Pusher payload
          // actually carries a value. When the live_return_eta flag is off,
          // the backend omits the field — preserving the prior value avoids
          // an unnecessary write, but the field will also never have been
          // populated in the first place on new orders, so the badge stays
          // hidden as intended.
          if (update.projectedCompletionAtLive !== undefined) {
            next.projectedCompletionAtLive = update.projectedCompletionAtLive
          }
          // ADR-0012 §5: same kill-switch semantics for the pickup-side field.
          // Absence on the wire means "no change" (post-pickup freeze or
          // live_pickup_eta off). Backend owns the freeze gate.
          if (update.projectedPickupAtLive !== undefined) {
            next.projectedPickupAtLive = update.projectedPickupAtLive
          }
          return next
        })
        set({ orders: updatedOrders })
      },

      /**
       * Apply a PICKUP_REALIZED Pusher broadcast to the matching order.
       * First-write-wins - existing values are not overwritten, matching
       * the server-side invariant in markRealisedPickup.
       */
      updateOrderRealisedPickup: (orderId, realisedPickupAt) => {
        if (!orderId || !realisedPickupAt) return
        const state = get()
        const updatedOrders = state.orders.map((order) => {
          if (order.id !== orderId) return order
          // First-write-wins: never overwrite an existing realised time.
          if (order.realisedPickupAt) return order
          return { ...order, realisedPickupAt }
        })
        set({ orders: updatedOrders })
      },

      /**
       * PLA-111 — flip berichtPdfStatus to "available" on the matching order.
       * Called by the Pusher REPORT_AVAILABLE handler. No-op when the order
       * is unknown or already marked available; FleetOrderRow shows the
       * download button whenever berichtPdfStatus === "available".
       */
      markOrderReportAvailable: (orderId) => {
        if (!orderId) return
        const state = get()
        let mutated = false
        const updatedOrders = state.orders.map((order) => {
          if (order.id !== orderId) return order
          if (order.berichtPdfStatus === "available") return order
          mutated = true
          return { ...order, berichtPdfStatus: "available" as const }
        })
        if (mutated) set({ orders: updatedOrders })
      },

      /**
       * Dismiss password change prompt for current session
       */
      dismissPasswordPrompt: () => {
        set({ showPasswordPrompt: false, passwordPromptDismissed: true })
      },

      /**
       * Mark initial password as changed in Firestore
       */
      markPasswordChanged: async () => {
        try {
          const { confirmPasswordChanged } = await _functionsModuleLoader.load()
          await confirmPasswordChanged()
          set({ changedInitialPassword: true, showPasswordPrompt: false })
        } catch (error) {
          console.error("[FleetHub] Error confirming password change:", error)
          // Still hide the prompt — password was changed in Firebase Auth even if Firestore update failed
          set({ showPasswordPrompt: false })
        }
      },
    }),
    {
      name: "FleetHubStore",
      // `activeOrders` is a Map<string, ActiveOrderTrackingData> and
      // `pollingIntervals` / `loadingCardTimeout` hold OS timer handles —
      // none of these are JSON-serializable. Without a custom serializer the
      // devtools middleware's `JSON.stringify` call enters infinite recursion
      // on non-serializable values, causing "Maximum call stack size exceeded"
      // (WEB-20). Replace them with safe representations for the DevTools panel.
      serialize: {
        replacer: (_key: string, value: unknown) => {
          if (value instanceof Map) {
            return { __type: "Map", size: (value as Map<unknown, unknown>).size }
          }
          // Timer handles are not serializable; show a placeholder.
          if (typeof value === "object" && value !== null && "ref" in (value as object)) {
            return "[TimerHandle]"
          }
          return value
        },
      },
    },
  ),
)

/**
 * Status to numeric phase mapping for live tracking
 */
const STATUS_TO_NUMERIC_PHASE: { [key: string]: number } = {
  new: 1,
  confirmed: 2,
  pickup_due: 2,
  picking_up: 3,
  at_hub: 4,
  ready_to_return: 5,
  returning: 6,
  completed: 7,
  no_show: 1,
  backlog: 1,
  cancelled: 1,
}

/**
 * Computed selectors for better performance
 */

/**
 * Get total number of pages
 */
export const selectTotalPages = (state: FleetHubState) =>
  Math.ceil(state.totalCount / state.pageSize)

/**
 * Check if there are any orders
 */
export const selectHasOrders = (state: FleetHubState) => state.orders.length > 0

/**
 * Get current order count
 */
export const selectOrderCount = (state: FleetHubState) => state.orders.length

/**
 * Check if date filter is active (for badge display)
 */
export const selectHasActiveDateFilter = (state: FleetHubState): boolean => {
  return hasActiveDateFilter(state.filters.dateRange)
}

/**
 * Check if number plate search is active
 */
export const selectIsSearchActive = (state: FleetHubState): boolean => {
  return state.numberPlateFilter.length > 0
}
